import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Divider,
  Typography,
  Box,
} from "@mui/material";
import {
  Dashboard,
  Code,
  AddCircle,
  Menu,
  Logout,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { logout } from "../redux/authSlice";
import { toggleDarkMode } from "../redux/darkModeSlice";

const Sidebar = () => {
  const [open, setOpen] = useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const darkMode = useSelector((state) => state.darkMode.isDarkMode);

  const drawerStyles = {
    width: open ? 250 : 80,
    transition: "width 0.3s ease-in-out",
    "& .MuiDrawer-paper": {
      width: open ? 250 : 80,
      transition: "width 0.3s ease-in-out",
      overflowX: "hidden",
      bgcolor: "background.paper",
      borderRight: "1px solid",
      borderColor: "divider",
    },
    "& .MuiListItemIcon-root": {
      color: "primary.main",
      minWidth: 40,
    },
    "& .MuiListItemButton-root": {
      px: 2,
      py: 1.5,
      "&:hover": {
        bgcolor: "action.hover",
      },
      "&.active": {
        bgcolor: "action.selected",
        borderRight: 3,
        borderColor: "primary.main",
        "& .MuiListItemIcon-root": {
          color: "primary.main",
        },
      },
    },
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const isActiveRoute = (path) => location.pathname === path;

  const handleDarkModeToggle = () => {
    dispatch(toggleDarkMode());
  };

  return (
    <Drawer variant="permanent" open={open} sx={drawerStyles}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {open && (
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Snippet-er
          </Typography>
        )}
        <IconButton
          onClick={() => setOpen(!open)}
          sx={{ color: "primary.main" }}
        >
          <Menu />
        </IconButton>
      </Box>
      <Divider />

      <List>
        {/* Dashboard */}
        <Tooltip title={!open ? "Dashboard" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/dashboard"
              className={isActiveRoute("/dashboard") ? "active" : ""}
            >
              <ListItemIcon>
                <Dashboard />
              </ListItemIcon>
              {open && <ListItemText primary="Dashboard" />}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/* My Snippets */}
        <Tooltip title={!open ? "My Snippets" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/my-snippets"
              className={isActiveRoute("/my-snippets") ? "active" : ""}
            >
              <ListItemIcon>
                <Code />
              </ListItemIcon>
              {open && <ListItemText primary="My Snippets" />}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/* Add Snippet */}
        <Tooltip title={!open ? "Add Snippet" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/add-snippet"
              className={isActiveRoute("/add-snippet") ? "active" : ""}
            >
              <ListItemIcon>
                <AddCircle />
              </ListItemIcon>
              {open && <ListItemText primary="Add Snippet" />}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        {/* Dark Mode Toggle */}
        <Tooltip title={!open ? "Toggle Theme" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton onClick={handleDarkModeToggle}>
              <ListItemIcon>
                {darkMode ? <Brightness7 /> : <Brightness4 />}
              </ListItemIcon>
              {open && (
                <ListItemText primary={darkMode ? "Light Mode" : "Dark Mode"} />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>

        <Divider />

        {/* Logout */}
        <Tooltip title={!open ? "Logout" : ""} placement="right">
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              {open && <ListItemText primary="Logout" />}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>
    </Drawer>
  );
};

export default Sidebar;
